/**
 * SECTION 06 — Data Classification & Information Protection
 *
 * Renders after Section 05 (Domain Results) and before Section 07
 * (Technical Validation). Skipped when the Reviewer has not selected
 * any data types for the assessment.
 *
 * Layout:
 *   1. Section title (single instance, never repeated)
 *   2. Short introductory paragraph
 *   3. Summary cards: Total + Critical / High / Moderate / Low
 *   4. Classification matrix (autoTable, badge styling, multi-page safe)
 *
 * Continuation pages render only the running header + a small
 * "Data Classification & Information Protection (continued)" caption
 * above the repeating table header — never the full section title.
 */
import type jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import {
  composeBusinessImpact,
  getDataTypeEntry,
} from "@/reports/telecom/dataClassificationCatalog";

export interface DataClassificationRow {
  data_type: string;
  default_sensitivity?: string | null;
  reviewer_sensitivity?: string | null;
  data_volume?: string | null;
  reviewer_notes?: string | null;
  reviewed?: boolean | null;
}

export interface DataClassificationContext {
  rows: DataClassificationRow[];
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  startPageNumber?: number;
  sectionNumber?: string;
}

// Classification badge palette — aligned with Top Findings risk badges.
const CLASS_BADGE: Record<string, { fill: RGB; text: RGB }> = {
  Critical: { fill: [153, 27, 27],  text: [255, 255, 255] }, // red-800
  High:     { fill: [220, 38, 38],  text: [255, 255, 255] }, // red-600
  Moderate: { fill: [245, 158, 11], text: [255, 255, 255] }, // amber-500
  Low:      { fill: [148, 163, 184], text: [255, 255, 255] }, // slate-400
};

// Summary-card accent (border + count color) per level.
const CARD_ACCENT: Record<string, RGB> = {
  Total:    REPORT_THEME.accent.rgb as RGB,
  Critical: [153, 27, 27],
  High:     [220, 38, 38],
  Moderate: [245, 158, 11],
  Low:      [100, 116, 139],
};

const ORDER = ["Critical", "High", "Moderate", "Low"] as const;

function effectiveSens(r: DataClassificationRow): string {
  return (r.reviewer_sensitivity || r.default_sensitivity || "").trim();
}

function normalizeSens(s: string): string {
  const t = s.toLowerCase();
  if (t === "critical") return "Critical";
  if (t === "high") return "High";
  if (t === "moderate" || t === "medium") return "Moderate";
  if (t === "low") return "Low";
  return s;
}

/** Paint a pill-style badge centered in the cell (matches Top Findings). */
function paintCellBadge(
  doc: jsPDF,
  cell: any,
  label: string,
  fill: RGB,
  text: RGB,
): void {
  const padX = 6;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  const tw = doc.getTextWidth(label);
  const bw = Math.min(tw + padX * 2, cell.width - 4);
  const bh = 14;
  const cx = cell.x + cell.width / 2;
  const cy = cell.y + cell.height / 2;
  const bx = cx - bw / 2;
  const by = cy - bh / 2;

  // Wipe underlying cell text (inset 1pt to keep cell borders intact).
  doc.setFillColor(
    cell.styles.fillColor?.[0] ?? 255,
    cell.styles.fillColor?.[1] ?? 255,
    cell.styles.fillColor?.[2] ?? 255,
  );
  doc.rect(cell.x + 1, cell.y + 1, cell.width - 2, cell.height - 2, "F");

  doc.setFillColor(...fill);
  doc.roundedRect(bx, by, bw, bh, 2, 2, "F");
  doc.setTextColor(...text);
  doc.text(label, cx, by + bh / 2 + 2.8, { align: "center" });

  // Restore cell borders clipped by the wipe.
  const lc = (cell.styles.lineColor as RGB) ?? (REPORT_THEME.border.rgb as RGB);
  const lw = (cell.styles.lineWidth as number) ?? 0.4;
  doc.setDrawColor(lc[0], lc[1], lc[2]);
  doc.setLineWidth(lw);
  doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
  doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
  doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
  doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);
}

export function renderDataClassification(
  doc: jsPDF,
  ctx: DataClassificationContext,
): number {
  const rows = (ctx.rows ?? []).filter((r) => r && r.data_type);
  if (rows.length === 0) {
    return ctx.startPageNumber ?? doc.getNumberOfPages();
  }

  doc.addPage();
  const firstPage = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const innerW = pageW - PAGE_MARGIN * 2;
  const sectionNumber = ctx.sectionNumber ?? "06";
  const sectionTitle = "Data Classification & Information Protection";

  // ── Page 1 chrome + section title ──────────────────────────────────
  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName || "Client",
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });
  let y = drawSectionTitle(doc, {
    sectionNumber,
    title: sectionTitle,
    continued: false,
  });

  // ── Introductory paragraph ─────────────────────────────────────────
  const intro =
    "The following table summarizes the primary data categories identified during the assessment, their sensitivity classification, expected business impact, and recommended protection measures.";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  const introLines = doc.splitTextToSize(intro, innerW);
  doc.text(introLines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
  y += introLines.length * 14 + 14;

  // ── Summary cards: Total + Critical / High / Moderate / Low ────────
  const counts: Record<string, number> = { Critical: 0, High: 0, Moderate: 0, Low: 0 };
  for (const r of rows) {
    const s = normalizeSens(effectiveSens(r));
    if (counts[s] != null) counts[s]++;
  }
  const cards: { label: string; value: number }[] = [
    { label: "Total",    value: rows.length },
    { label: "Critical", value: counts.Critical },
    { label: "High",     value: counts.High },
    { label: "Moderate", value: counts.Moderate },
    { label: "Low",      value: counts.Low },
  ];
  const cardGap = 10;
  const cardW = (innerW - cardGap * (cards.length - 1)) / cards.length;
  const cardH = 60;
  cards.forEach((c, i) => {
    const x = PAGE_MARGIN + i * (cardW + cardGap);
    const accent = CARD_ACCENT[c.label] ?? (REPORT_THEME.accent.rgb as RGB);

    // Card body
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, cardW, cardH, 4, 4, "FD");

    // Accent top stripe
    doc.setFillColor(...accent);
    doc.rect(x, y, cardW, 3, "F");

    // Value
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...accent);
    doc.text(String(c.value), x + 10, y + 32);

    // Label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.text(c.label.toUpperCase(), x + 10, y + 48);

    // Sublabel
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.text("data categories", x + 10, y + 56);
  });
  y += cardH + 18;

  // ── Build table body ───────────────────────────────────────────────
  const sevRank = (r: DataClassificationRow) => {
    const s = normalizeSens(effectiveSens(r));
    const idx = (ORDER as readonly string[]).indexOf(s);
    return idx === -1 ? 99 : idx;
  };
  const sortedRows = [...rows].sort((a, b) => {
    const d = sevRank(a) - sevRank(b);
    return d !== 0 ? d : a.data_type.localeCompare(b.data_type);
  });

  const sensByRow: string[] = [];
  const body = sortedRows.map((r) => {
    const sens = normalizeSens(effectiveSens(r)) || "—";
    sensByRow.push(sens);
    const vol = (r.data_volume || "").trim() || "—";
    const entry = getDataTypeEntry(r.data_type);
    const impact = composeBusinessImpact({
      sensitivity: sens === "—" ? null : sens,
      volume: vol === "—" ? null : vol,
      drivers: entry.drivers,
    });
    // Compact controls — comma-separated keeps row heights tight.
    const controls = entry.requiredControls.join("; ");
    return [
      r.data_type,
      sens,
      vol,
      `${entry.description}\n\n${impact}`,
      controls,
    ];
  });

  // Column widths sum exactly to innerW (532 at letter). Each width is sized
  // so that every header label fits on a single line at the header font size
  // (8.2pt bold) — no mid-word breaks anywhere in the table head.
  // Data Category 88 / Classification 84 / Volume 50 / Desc 150 / Controls 160
  const COL_W = {
    cat: 88,
    cls: 84,
    vol: 50,
    desc: 150,
    ctl: innerW - (88 + 84 + 50 + 150),
  };

  // Reserve enough top margin on continuation pages for:
  //   header strip (44) + continued caption (~24) + gap = 90
  const CONTINUATION_TOP = 100;

  autoTable(doc, {
    startY: y,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, top: CONTINUATION_TOP, bottom: 50 },
    head: [[
      "Data Category",
      "Classification",
      "Volume",
      "Description & Business Impact",
      "Required Security Controls",
    ]],
    body,
    theme: "grid",
    showHead: "everyPage",
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: 5,
      overflow: "linebreak",
      valign: "top",
      textColor: REPORT_THEME.textPrimary.rgb as any,
      lineColor: REPORT_THEME.border.rgb as any,
      lineWidth: 0.4,
    },
    headStyles: {
      fillColor: REPORT_THEME.accent.rgb as any,
      textColor: REPORT_THEME.textOnNavy.rgb as any,
      fontStyle: "bold" as any,
      fontSize: 8.2,
      halign: "left",
      valign: "middle",
      cellPadding: { top: 6, right: 4, bottom: 6, left: 5 },
      overflow: "linebreak",
    },
    alternateRowStyles: {
      fillColor: REPORT_THEME.surfaceAlt.rgb as any,
    },
    columnStyles: {
      0: { cellWidth: COL_W.cat, fontStyle: "bold" as any },
      1: { cellWidth: COL_W.cls, halign: "center", valign: "middle", overflow: "visible" },
      2: { cellWidth: COL_W.vol, halign: "center", valign: "middle", overflow: "visible" },
      3: { cellWidth: COL_W.desc },
      4: { cellWidth: COL_W.ctl },
    },
    rowPageBreak: "avoid",
    didParseCell: (data: any) => {
      // Keep Classification & Volume on a single line regardless of value.
      if (data.section === "body" && (data.column.index === 1 || data.column.index === 2)) {
        data.cell.styles.overflow = "visible";
      }
      if (data.section === "head") {
        // Allow header text to wrap inside the cell rather than overflow the
        // page margin (prevents right-edge clipping of "Required Security Controls").
        data.cell.styles.overflow = "linebreak";
      }
    },
    didDrawCell: (data: any) => {
      if (data.section === "body" && data.column.index === 1) {
        const sens = sensByRow[data.row.index];
        const palette = CLASS_BADGE[sens];
        if (palette) {
          paintCellBadge(doc, data.cell, sens, palette.fill, palette.text);
        }
      }
    },
    didDrawPage: () => {
      const current =
        (doc as any).internal?.getCurrentPageInfo?.()?.pageNumber ??
        doc.getNumberOfPages();
      if (current === firstPage) return;

      // Continuation page — header + small "(continued)" caption.
      drawRunningHeader(doc, {
        clientName: ctx.clientOrgName || "Client",
        frameworks: ctx.frameworks ?? null,
        methodology: ctx.methodology ?? null,
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...REPORT_THEME.navy.rgb);
      doc.text(`${sectionTitle} (continued)`, PAGE_MARGIN, 76);
      doc.setDrawColor(...REPORT_THEME.gold.rgb);
      doc.setLineWidth(1);
      doc.line(PAGE_MARGIN, 82, PAGE_MARGIN + 64, 82);
    },
  } as any);

  // ── Footers on all pages we produced ───────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = firstPage; p <= totalPages; p++) {
    doc.setPage(p);
    drawPageFooter(doc, p);
  }

  return totalPages;
}
